import "./PageDetailView.css";

import { memo, useContext, useEffect, useRef, useState } from "react";

import { ErrorScreen, LoadingTextIndicator } from "components/LoadingScreen";
import { WAITED_TOO_LONG_TIMEOUT_MS } from "components/constants";
import { PageWorksheet } from "components/materials/page/content/PageWorksheet";
import { t } from "i18n/i18n";
import { DocViewContext, type PageTabVariant } from "providers/DocViewProvider";
import { SHAREDB_STATUS_MESSAGE, ShareDBContext } from "providers/ShareDBProvider";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useCanLoadDoc, useDocContext } from "utils/worksheet";

type PageDetailViewProps = {
  pageId: string;
  selectedAccessId?: string | null;
  selectedTabVariant?: PageTabVariant | null;
  isProjecting?: boolean;
  isFeaturing?: boolean;
  isStaticContent?: boolean;
};

// TODO: Potentially merge parts of PageDetailView, PageOverview and PageWorksheet, since they
// all operate at the same level of abstraction, and add toolbars/headers/banners to the top or side
// of a worksheet. Preferably, all the added tabs/toolbars/headers/banners/sidebars would be separate
// components, reducing the code content inside PageDetailView/PageOverview/PageWorksheet, allowing
// us to combine the three into a single component.
export const PageDetailView = memo(
  ({
    pageId,
    selectedAccessId,
    selectedTabVariant,
    isFeaturing = false,
    isProjecting = false,
    isStaticContent = false,
  }: PageDetailViewProps) => {
    const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["worksheet_id"]);
    const shareDBContext = useContext(ShareDBContext);
    const shareDBStatus = shareDBContext?.shareDBStatus ?? "connecting";
    const retryConnection = shareDBContext?.retryShareDBConnection ?? (() => {});
    const [initError, setInitError] = useState<string | null>(null);
    const isConnected = shareDBStatus === "connected";
    const initTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

    const canLoadDoc = useCanLoadDoc(pageId);

    const pageContext = useDocContext(
      pageId,
      selectedAccessId,
      selectedTabVariant,
      isProjecting,
      isFeaturing,
      isStaticContent,
    );

    useEffect(() => {
      if (canLoadDoc && isConnected && !pageContext.canWriteDoc) {
        initTimeout.current = setTimeout(() => {
          setInitError("Timeout");
        }, WAITED_TOO_LONG_TIMEOUT_MS);
      }

      if (pageContext.canWriteDoc && initTimeout.current) {
        clearTimeout(initTimeout.current);
        initTimeout.current = undefined;
        setInitError(null);
      }
      return () => clearTimeout(initTimeout.current);
    }, [canLoadDoc, isConnected, pageContext.canWriteDoc]);

    if (!isConnected) {
      return (
        <div className="worksheet-page">
          <div className="h-full w-full">
            {["connecting", "disconnected"].includes(shareDBStatus) ? (
              <LoadingTextIndicator>{SHAREDB_STATUS_MESSAGE[shareDBStatus]}</LoadingTextIndicator>
            ) : (
              <ErrorScreen
                retryText={t("worksheet.error.try_reconnect")}
                onRetry={() => {
                  retryConnection();
                  setInitError(null);
                }}
              >
                <div className="body-m">{SHAREDB_STATUS_MESSAGE[shareDBStatus]}</div>
              </ErrorScreen>
            )}
          </div>
        </div>
      );
    }

    if (!page?.worksheet_id) {
      return <h3 className="m-4">There is an issue with this page. Please contact support.</h3>;
    }

    if (!canLoadDoc) {
      return (
        <LoadingTextIndicator>{t("worksheet.loading.waiting_on_permissions")}</LoadingTextIndicator>
      );
    }

    if (initError) {
      return (
        <div className="worksheet-page">
          <div className="h-full w-full">
            <ErrorScreen
              retryText={t("worksheet.error.try_again")}
              onRetry={() => {
                setInitError(null);
                retryConnection();
              }}
            >
              <div className="body-m">{t("worksheet.error.waiting_on_permissions")}</div>
            </ErrorScreen>
          </div>
        </div>
      );
    }

    if (!pageContext.canWriteDoc) {
      return (
        <LoadingTextIndicator>{t("worksheet.loading.waiting_on_permissions")}</LoadingTextIndicator>
      );
    }

    return (
      <DocViewContext.Provider value={pageContext}>
        <PageWorksheet
          pageId={pageId}
          selectedAccessId={selectedAccessId}
          selectedTabVariant={selectedTabVariant ?? undefined}
        />
      </DocViewContext.Provider>
    );
  },
);

PageDetailView.displayName = "PageDetailView";
